import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ChevronLeft, ChevronRight, CalendarDays, Clock, User, Package,
  DollarSign, Plus, Check, Eye, Mail, MessageSquare, Ban, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { CancelBookingModal } from './CancelBookingModal';
import { EditBookingDialog } from './EditBookingDialog';
import { BookingStatusBadge } from './BookingStatusBadge';
import useMessageStream from '../hooks/useMessageStream';

// Lifecycle groups that drive calendar rendering (mirror booking_status.py).
const HIDDEN_LIFECYCLE = new Set(['cancelled', 'cancelled_non_payment', 'hold_expired', 'past_due', 'no_show']);
const TENTATIVE_LIFECYCLE = new Set(['pending_payment']);
const isHiddenBooking = (b) => {
  if (b?.lifecycle_status) return HIDDEN_LIFECYCLE.has(b.lifecycle_status);
  return b?.status === 'cancelled' || b?.status === 'deleted';
};
const isTentativeBooking = (b) => {
  if (b?.lifecycle_status) return TENTATIVE_LIFECYCLE.has(b.lifecycle_status);
  return b?.status === 'pending_hold' || b?.status === 'pending';
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIME_LABELS = { '10:00': '10 AM', '13:00': '1 PM', '16:00': '4 PM' };
const PKG_SHORT = { 'Create Party': 'C', 'Celebrate Party': 'CL', 'Signature Edible Party': 'S' };
const PKG_COLORS = { 'Create Party': '#FF3B6B', 'Celebrate Party': '#06D6A0', 'Signature Edible Party': '#FFD166' };
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_DAILY_BOOKINGS = 3; // 3 time slots

const getMonthDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const days = [];

  // Previous month padding
  const prevLast = new Date(year, month, 0).getDate();
  for (let i = startPad - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month - 1, prevLast - i), isCurrentMonth: false });
  }

  // Current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }

  // Next month padding (fill to 6 rows)
  const remaining = 42 - days.length;
  for (let d = 1; d <= remaining; d++) {
    days.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
  }

  return days;
};

const formatDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const AdminCalendarView = ({ onBookingClick, onSendReminder }) => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // month, week
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/packages`).then((r) => setPackages(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true }).then((r) => setEmployees(r.data || [])).catch(() => {});
  }, []);

  const todayStr = formatDate(today);
  const monthDays = useMemo(() => getMonthDays(currentYear, currentMonth), [currentYear, currentMonth]);
  const monthName = new Date(currentYear, currentMonth).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch bookings for the visible range (prev month to next month)
      const start = new Date(currentYear, currentMonth - 1, 1);
      const end = new Date(currentYear, currentMonth + 2, 0);
      const res = await axios.get(`${API_URL}/api/admin/bookings`, {
        params: {
          date_from: formatDate(start),
          date_to: formatDate(end)
        },
        withCredentials: true
      });
      // Show cancelled (with strikethrough style); hide soft-deleted.
      setBookings(res.data.filter(b => b.status !== 'deleted' && !b.deleted_at));
    } catch (e) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchBookings, 60000);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  // Real-time: refetch when a booking's status changes anywhere (payment,
  // cron hold-expiry, admin edit) so tentative/confirmed/hidden stays live.
  useMessageStream((evt) => {
    if (!evt) return;
    if (evt.type === 'calendar_changed'
      || evt.type === 'booking_updated'
      || (evt.type === 'crm_changed' && evt.section === 'bookings')) {
      fetchBookings();
    }
  }, { enabled: true });

  // Group bookings by date — hidden lifecycle statuses (cancelled / expired /
  // no-show / past-due) are dropped so they don't clutter the grid or count
  // toward a day's occupancy (keeps availability colouring accurate).
  const bookingsByDate = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (isHiddenBooking(b)) return;
      if (!map[b.date]) map[b.date] = [];
      map[b.date].push(b);
    });
    return map;
  }, [bookings]);

  const goToday = () => { setCurrentYear(today.getFullYear()); setCurrentMonth(today.getMonth()); };
  const goPrev = () => {
    if (currentMonth === 0) { setCurrentYear(y => y - 1); setCurrentMonth(11); }
    else setCurrentMonth(m => m - 1);
  };
  const goNext = () => {
    if (currentMonth === 11) { setCurrentYear(y => y + 1); setCurrentMonth(0); }
    else setCurrentMonth(m => m + 1);
  };

  const getDayCellColor = (dateStr, isPast, isCurrentMonth) => {
    if (!isCurrentMonth) return 'bg-gray-50/50';
    if (isPast) return 'bg-gray-50';
    const count = (bookingsByDate[dateStr] || []).length;
    if (count === 0) return 'bg-[#06D6A0]/8';
    if (count <= 2) return 'bg-[#FFD166]/10';
    if (count >= MAX_DAILY_BOOKINGS) return 'bg-[#FF3B6B]/8';
    return 'bg-orange-50';
  };

  const openDayDetail = (dayObj) => {
    setSelectedDay(dayObj);
    setDayModalOpen(true);
  };

  // Week view: only show the current week row
  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    const d = new Date(currentYear, currentMonth, today.getDate());
    const dayOfWeek = d.getDay();
    const start = new Date(d); start.setDate(d.getDate() - dayOfWeek);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const dt = new Date(start); dt.setDate(start.getDate() + i);
      days.push({ date: dt, isCurrentMonth: dt.getMonth() === currentMonth });
    }
    return days;
  }, [viewMode, currentYear, currentMonth, today]);

  const displayDays = viewMode === 'week' ? weekDays : monthDays;
  const gridRows = viewMode === 'week' ? 'grid-rows-1' : 'grid-rows-6';

  return (
    <div className="w-full" data-testid="admin-full-calendar">
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {monthName}
          </h2>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={goPrev} className="rounded-full w-8 h-8 p-0 border-[#E8E5DF]" data-testid="cal-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={goToday} className="rounded-full px-3 border-[#E8E5DF] text-xs" data-testid="cal-today">
              Today
            </Button>
            <Button size="sm" variant="outline" onClick={goNext} className="rounded-full w-8 h-8 p-0 border-[#E8E5DF]" data-testid="cal-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Legend */}
          <div className="hidden lg:flex items-center gap-3 text-xs text-[#6B6B7A]">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#06D6A0]/30" /> Available</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#FFD166]/30" /> 1-2 Bookings</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#FF3B6B]/20" /> Full</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gray-200" /> Past</span>
          </div>

          {/* View Toggle */}
          <div className="flex rounded-full border border-[#E8E5DF] overflow-hidden">
            <button onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'month' ? 'bg-[#FF3B6B] text-white' : 'text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
              data-testid="view-month">Month</button>
            <button onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'week' ? 'bg-[#FF3B6B] text-white' : 'text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
              data-testid="view-week">Week</button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B6B]" />
        </div>
      )}

      {!loading && (
        <div className="border border-[#E8E5DF] rounded-2xl overflow-hidden bg-white">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-[#E8E5DF]">
            {DAYS.map(day => (
              <div key={day} className="py-3 text-center text-xs font-semibold text-[#6B6B7A] uppercase tracking-wider bg-[#FFFDF8]">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className={`grid grid-cols-7 ${gridRows}`}>
            {displayDays.map((dayObj, idx) => {
              const dateStr = formatDate(dayObj.date);
              const isToday = dateStr === todayStr;
              const isPast = dayObj.date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const dayBookings = bookingsByDate[dateStr] || [];
              const count = dayBookings.length;
              const cellColor = getDayCellColor(dateStr, isPast, dayObj.isCurrentMonth);
              const isFull = count >= MAX_DAILY_BOOKINGS;

              return (
                <div
                  key={idx}
                  onClick={() => dayObj.isCurrentMonth && openDayDetail(dayObj)}
                  className={`
                    ${cellColor} border-b border-r border-[#E8E5DF] p-2 cursor-pointer
                    transition-all hover:ring-2 hover:ring-[#FF3B6B]/30 hover:z-10 relative
                    ${viewMode === 'week' ? 'min-h-[200px]' : 'min-h-[110px]'}
                    ${!dayObj.isCurrentMonth ? 'opacity-40' : ''}
                  `}
                  data-testid={`cal-day-${dateStr}`}
                >
                  {/* Date Number */}
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-semibold leading-none ${
                      isToday
                        ? 'w-7 h-7 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center'
                        : isPast ? 'text-[#6B6B7A]/50' : 'text-[#1E1E24]'
                    }`}>
                      {dayObj.date.getDate()}
                    </span>

                    {/* Status Badge */}
                    {dayObj.isCurrentMonth && !isPast && count === 0 && (
                      <span className="text-[10px] font-semibold text-[#06D6A0] bg-[#06D6A0]/15 px-1.5 py-0.5 rounded-full" data-testid={`badge-available-${dateStr}`}>
                        Available
                      </span>
                    )}
                    {dayObj.isCurrentMonth && count > 0 && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        isFull ? 'bg-[#FF3B6B]/15 text-[#FF3B6B]' : count >= 2 ? 'bg-orange-100 text-orange-700' : 'bg-[#FFD166]/20 text-[#B8860B]'
                      }`}>
                        {count} {count === 1 ? 'party' : 'parties'}
                      </span>
                    )}
                    {dayObj.isCurrentMonth && isPast && count === 0 && (
                      <span className="text-[10px] text-[#6B6B7A]/40">Past</span>
                    )}
                  </div>

                  {/* Booking Cards (mini) */}
                  <div className="space-y-0.5">
                    {dayBookings.slice(0, viewMode === 'week' ? 5 : 2).map((b) => {
                      const isTentative = isTentativeBooking(b);
                      const accent = b.status_color || PKG_COLORS[b.package_name] || '#6B6B7A';
                      return (
                      <div
                        key={b.booking_id}
                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] leading-tight truncate ${isTentative ? 'border border-dashed opacity-75' : ''}`}
                        style={{ backgroundColor: `${accent}15`, borderColor: isTentative ? accent : undefined }}
                        title={`${b.status_label ? '[' + b.status_label + '] ' : ''}${b.child_name} - ${b.package_name} (${b.customer_name})${b.status_note ? ' · ' + b.status_note : ''}`}
                        data-testid={`cal-booking-${b.booking_id}${isTentative ? '-tentative' : ''}`}
                      >
                        <span className="font-bold" style={{ color: accent }}>
                          {TIME_LABELS[b.time_slot] || b.time_slot}
                        </span>
                        <span className="truncate text-[#1E1E24]">{b.child_name}</span>
                        <span className="ml-auto font-bold text-[9px] px-1 rounded"
                          style={{ backgroundColor: `${accent}25`, color: accent }}>
                          {isTentative ? 'H' : (PKG_SHORT[b.package_name] || 'P')}
                        </span>
                        {!isTentative && b.payment_status === 'fully_paid' && <Check className="w-2.5 h-2.5 text-[#06D6A0] flex-shrink-0" />}
                        {!isTentative && b.payment_status === 'deposit_paid' && <DollarSign className="w-2.5 h-2.5 text-[#FFD166] flex-shrink-0" />}
                      </div>
                      );
                    })}
                    {count > (viewMode === 'week' ? 5 : 2) && (
                      <p className="text-[9px] text-[#6B6B7A] pl-1">+{count - (viewMode === 'week' ? 5 : 2)} more</p>
                    )}
                  </div>

                  {/* Quick Book button for available future dates */}
                  {dayObj.isCurrentMonth && !isPast && count === 0 && viewMode === 'week' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); window.open(`/book?date=${dateStr}`, '_blank'); }}
                      className="mt-1 flex items-center gap-1 text-[10px] text-[#06D6A0] hover:text-[#05B88A] font-medium"
                      data-testid={`quick-book-${dateStr}`}
                    >
                      <Plus className="w-3 h-3" /> Book
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day Detail Modal */}
      <Dialog open={dayModalOpen} onOpenChange={setDayModalOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          {selectedDay && (() => {
            const dateStr = formatDate(selectedDay.date);
            const dayBookings = bookingsByDate[dateStr] || [];
            const isPast = selectedDay.date < new Date(today.getFullYear(), today.getMonth(), today.getDate());

            return (
              <>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {selectedDay.date.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Day Summary */}
                  <div className="flex items-center gap-3">
                    {dayBookings.length === 0 && !isPast && (
                      <span className="px-3 py-1 bg-[#06D6A0]/15 text-[#06D6A0] rounded-full text-sm font-semibold" data-testid="day-available-badge">Available</span>
                    )}
                    {dayBookings.length > 0 && (
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        dayBookings.length >= MAX_DAILY_BOOKINGS ? 'bg-[#FF3B6B]/15 text-[#FF3B6B]' : 'bg-[#FFD166]/15 text-[#B8860B]'
                      }`}>
                        {dayBookings.length} {dayBookings.length === 1 ? 'Booking' : 'Bookings'}
                      </span>
                    )}
                    {isPast && dayBookings.length === 0 && (
                      <span className="px-3 py-1 bg-gray-100 text-[#6B6B7A] rounded-full text-sm">Past Date</span>
                    )}

                    {/* Slot availability */}
                    {!isPast && dayBookings.length < MAX_DAILY_BOOKINGS && (
                      <span className="text-xs text-[#6B6B7A]">{MAX_DAILY_BOOKINGS - dayBookings.length} slot{MAX_DAILY_BOOKINGS - dayBookings.length > 1 ? 's' : ''} open</span>
                    )}
                  </div>

                  {/* Time Slot Grid */}
                  {!isPast && (
                    <div className="grid grid-cols-3 gap-2">
                      {Object.entries(TIME_LABELS).map(([slot, label]) => {
                        const slotBooking = dayBookings.find(b => b.time_slot === slot);
                        return (
                          <div key={slot} className={`p-3 rounded-xl border text-center ${
                            slotBooking
                              ? 'border-[#FF3B6B]/30 bg-[#FF3B6B]/5'
                              : 'border-[#06D6A0]/30 bg-[#06D6A0]/5'
                          }`}>
                            <p className="text-xs font-semibold text-[#1E1E24]">{label}</p>
                            {slotBooking ? (
                              <p className="text-[10px] text-[#FF3B6B] font-medium mt-1 truncate">{slotBooking.child_name}</p>
                            ) : (
                              <p className="text-[10px] text-[#06D6A0] font-medium mt-1">Open</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Booking Details */}
                  {dayBookings.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-[#1E1E24]">Bookings</h4>
                      {dayBookings.map(b => (
                        <div key={b.booking_id} className="p-4 rounded-xl border border-[#E8E5DF] bg-[#FFFDF8]">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-[#1E1E24]">{b.child_name}'s Party</p>
                              <p className="text-xs text-[#6B6B7A]">{b.customer_name} | {b.customer_phone}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => { setDayModalOpen(false); onBookingClick?.(b.booking_id); }} className="h-7 w-7 p-0" data-testid={`modal-view-${b.booking_id}`}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={async () => {
                                  try {
                                    const r = await axios.get(`${API_URL}/api/admin/bookings/${b.booking_id}`, { withCredentials: true });
                                    setEditTarget(r.data?.booking || b);
                                  } catch { setEditTarget(b); }
                                  setDayModalOpen(false);
                                  setEditOpen(true);
                                }}
                                className="h-7 w-7 p-0 text-[#6366F1]"
                                title="Edit booking"
                                data-testid={`cal-edit-${b.booking_id}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onSendReminder?.(b, 'email')}
                                disabled={!b.customer_email}
                                className="h-7 w-7 p-0 text-[#FF3B6B] hover:bg-[#FF3B6B]/10 disabled:opacity-40"
                                title={b.customer_email ? 'Send email to customer (choose template)' : 'No email on file'}
                                data-testid={`cal-email-${b.booking_id}`}
                              >
                                <Mail className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setCancelTarget(b); setDayModalOpen(false); setCancelOpen(true); }}
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                                title="Cancel booking"
                                data-testid={`cal-cancel-${b.booking_id}`}
                              >
                                <Ban className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {b.lifecycle_status && (
                              <BookingStatusBadge status={b.lifecycle_status} note={b.status_note} testid={`cal-modal-status-${b.booking_id}`} />
                            )}
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{ backgroundColor: `${PKG_COLORS[b.package_name] || '#6B6B7A'}15`, color: PKG_COLORS[b.package_name] || '#6B6B7A' }}>
                              {b.package_name}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                              <Clock className="w-3 h-3 inline mr-1" />{TIME_LABELS[b.time_slot]}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-[#6B6B7A]">
                              <User className="w-3 h-3 inline mr-1" />{b.guest_count} guests
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              b.payment_status === 'fully_paid' ? 'bg-green-100 text-green-700'
                              : b.payment_status === 'deposit_paid' ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                            }`}>
                              {b.payment_status === 'fully_paid' ? 'Paid' : b.payment_status === 'deposit_paid' ? 'Deposit' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Quick Book Action */}
                  {!isPast && dayBookings.length < MAX_DAILY_BOOKINGS && (
                    <Button
                      onClick={() => { setDayModalOpen(false); window.open(`/book?date=${dateStr}`, '_blank'); }}
                      className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full"
                      data-testid="modal-quick-book"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Book a Party for This Date
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <CancelBookingModal
        booking={cancelTarget}
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onSuccess={() => { setCancelOpen(false); fetchBookings(); }}
      />

      <EditBookingDialog
        booking={editTarget}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); fetchBookings(); }}
        packages={packages}
        employees={employees}
      />
    </div>
  );
};

export default AdminCalendarView;
